import React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '../view';
import {visuallyHidden} from '../utilStyles/visuallyHidden';
import {css, external} from '../css';
import type {TypographyEmphasis, TypographyTone} from './typography';

type LabelElement = 'label' | 'legend';
type LabelSize = 'large' | 'medium' | 'small';

const DEFAULT_SIZE = 'medium';
const DEFAULT_EMPHASIS = 'emphasized';
const DEFAULT_TONE = 'default';

export type LabelProps = View.IntrinsicElement<
  'label',
  {
    /**
     * Used to refer to the id of the form control this input is labeling.
     * @external
     */
    htmlFor?: string;
    hidden?: React.HTMLAttributes<HTMLLabelElement>['hidden'];
    id?: string;
    children?: React.ReactNode;
    /**
     * The type of label to use. This will determine the semantic meaning of the label.
     *
     * default: `label`
     */
    as: LabelElement;
    /**
     * Use different sizes to create clear hierarchies.
     */
    size: LabelSize;
    /**
     * Controls typographic prominence.
     */
    emphasis: TypographyEmphasis;
    /**
     * Controls text color prominence.
     */
    tone: TypographyTone;
  }
>;

export const LabelConfig = createViewConfig({
  props: {} as LabelProps,
  name: 'Label',
  flattens: true,
  defaults: {
    as: 'label',
    hidden: false,
    size: DEFAULT_SIZE,
    emphasis: DEFAULT_EMPHASIS,
    tone: DEFAULT_TONE,
  },
});

const typographyStyles = {
  large: {
    normal: css({font: 'label.large'}),
    emphasized: css({font: 'label.large.emphasized'}),
  },
  medium: {
    normal: css({font: 'label.medium'}),
    emphasized: css({font: 'label.medium.emphasized'}),
  },
  small: {
    normal: css({font: 'label.small'}),
    emphasized: css({font: 'label.small.emphasized'}),
  },
};

const toneStyles = {
  default: css({color: 'text'}),
  subdued: css({color: 'subdued'}),
};

const baseStyles = css({color: 'text'});

const elements: Record<LabelElement, React.ElementType> = {
  label: view.label,
  legend: view.legend,
};

export const Label = LabelConfig.createView(
  ({as, hidden, size, emphasis, tone, ...props}) => {
    const LabelTag = elements[as];
    const styles = React.useMemo(
      () => [
        baseStyles,
        typographyStyles[size ?? DEFAULT_SIZE][emphasis ?? DEFAULT_EMPHASIS],
        toneStyles[tone ?? DEFAULT_TONE],
        ...(hidden ? [visuallyHidden] : []),
      ],
      [hidden, size, emphasis, tone],
    );

    return <LabelTag css={external(styles)} {...props} />;
  },
);
